import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Check, X, AlertTriangle, Circle } from 'lucide-react';
import AddVehicleModal from './AddVehicleModal';
import DVIRModal from './DVIRModal';
import API_URL from '../../config';
import '../../App.css';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicle, setVehicle] = useState(null);
  const [dvirs, setDVIRs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDVIRModal, setShowDVIRModal] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    // Check if we should show DVIR modal from navigation state
    if (location.state?.showDVIR) {
      setShowDVIRModal(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchVehicle = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/vehicles/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setVehicle(response.data);
    } catch (err) {
      console.error('Error fetching vehicle:', err);
      setError('Failed to load vehicle details');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchDVIRs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/vehicles/${id}/dvirs`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDVIRs(response.data);
    } catch (err) {
      console.error('Error fetching DVIRs:', err);
    }
  }, [id]);

  const fetchDrivers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/drivers`, {
        headers: authHeaders(),
      });
      setDrivers(response.data || []);
    } catch (err) {
      console.error('Error fetching drivers:', err);
    }
  }, []);

  // Existing GET /vehicles already includes assignedDriverId — no new endpoint.
  const fetchFleetVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vehicles`, {
        headers: authHeaders(),
      });
      setFleetVehicles(response.data || []);
    } catch (err) {
      console.error('Error fetching vehicles for assignment map:', err);
    }
  }, []);

  useEffect(() => {
    fetchVehicle();
    fetchDVIRs();
    fetchDrivers();
    fetchFleetVehicles();
  }, [fetchVehicle, fetchDVIRs, fetchDrivers, fetchFleetVehicles]);

  const updateAssignedDriver = async (assignedDriverId) => {
    try {
      setIsSavingAssignment(true);
      setAssignmentError('');
      const response = await axios.put(
        `${API_URL}/api/vehicles/${id}`,
        { assignedDriverId },
        { headers: authHeaders() },
      );
      setVehicle(response.data);
      setIsAssignOpen(false);
      setSelectedDriverId('');
      setShowRemoveConfirm(false);
      await fetchFleetVehicles();
    } catch (err) {
      console.error('Error updating assigned driver:', err);
      const msg = err.response?.data?.message;
      setAssignmentError(
        Array.isArray(msg)
          ? msg.join(', ')
          : msg || 'Failed to update assigned driver. Please try again.',
      );
    } finally {
      setIsSavingAssignment(false);
    }
  };

  const handleAssignSave = () => {
    if (!selectedDriverId) return;
    updateAssignedDriver(selectedDriverId);
  };

  const handleRemoveDriver = () => {
    updateAssignedDriver(null);
  };

  const openAssignFlow = () => {
    setAssignmentError('');
    setShowRemoveConfirm(false);
    setSelectedDriverId(vehicle?.assignedDriverId || '');
    setIsAssignOpen(true);
    fetchDrivers();
    fetchFleetVehicles();
  };

  const getInspectionStatus = () => {
    if (!vehicle?.lastInspectionDate) {
      return { status: 'not_set', label: 'Not Set', color: '#6B7280', days: null };
    }

    const lastInspection = new Date(vehicle.lastInspectionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lastInspection.setHours(0, 0, 0, 0);

    const daysSinceInspection = Math.floor(
      (today.getTime() - lastInspection.getTime()) / (1000 * 60 * 60 * 24)
    );
    const daysUntilDue = 365 - daysSinceInspection;

    if (daysSinceInspection > 365) {
      return {
        status: 'overdue',
        label: 'Overdue',
        color: '#EF4444',
        days: Math.abs(daysSinceInspection - 365),
      };
    } else if (daysSinceInspection >= 350) {
      return {
        status: 'due_soon',
        label: 'Due Soon',
        color: '#F59E0B',
        days: daysUntilDue,
      };
    } else {
      return {
        status: 'valid',
        label: 'Valid',
        color: '#10B981',
        days: daysUntilDue,
      };
    }
  };

  const getRegistrationStatus = () => {
    if (!vehicle?.registrationExpirationDate) {
      return { status: 'not_set', label: 'Not Set', color: '#6B7280', days: null };
    }

    const expiration = new Date(vehicle.registrationExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiration.setHours(0, 0, 0, 0);

    const daysUntilExpiration = Math.ceil(
      (expiration.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiration < 0) {
      return {
        status: 'expired',
        label: 'Expired',
        color: '#EF4444',
        days: Math.abs(daysUntilExpiration),
      };
    } else if (daysUntilExpiration <= 30) {
      return {
        status: 'expiring',
        label: 'Expiring',
        color: '#F59E0B',
        days: daysUntilExpiration,
      };
    } else {
      return {
        status: 'valid',
        label: 'Valid',
        color: '#10B981',
        days: daysUntilExpiration,
      };
    }
  };

  const getReadinessBreakdown = () => {
    if (!vehicle) return [];
    const breakdown = [];

    if (vehicle.lastInspectionDate && vehicle.daysUntilInspectionDue > 0) {
      breakdown.push({ label: 'Inspection Current', points: 30, earned: true });
    } else {
      breakdown.push({ label: 'Inspection Current', points: 30, earned: false });
    }

    if (vehicle.registrationStatus === 'valid') {
      breakdown.push({ label: 'Registration Current', points: 20, earned: true });
    } else {
      breakdown.push({ label: 'Registration Current', points: 20, earned: false });
    }

    if (!vehicle.hasOpenDefects) {
      breakdown.push({ label: 'No Open Defects', points: 20, earned: true });
    } else {
      breakdown.push({ label: 'No Open Defects', points: 20, earned: false });
    }

    if (vehicle.recentDVIRCount > 0) {
      breakdown.push({ label: 'Recent DVIR', points: 15, earned: true });
    } else {
      breakdown.push({ label: 'Recent DVIR', points: 15, earned: false });
    }

    if (vehicle.isActive) {
      breakdown.push({ label: 'Active Status', points: 15, earned: true });
    } else {
      breakdown.push({ label: 'Active Status', points: 15, earned: false });
    }

    return breakdown;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading vehicle details...</p>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div className="page-container">
        <div className="dashboard-error">
          <p>{error || 'Vehicle not found'}</p>
          <button className="btn" onClick={() => navigate('/vehicles')}>
            Back to Vehicles
          </button>
        </div>
      </div>
    );
  }

  const inspectionStatus = getInspectionStatus();
  const registrationStatus = getRegistrationStatus();
  const readinessBreakdown = getReadinessBreakdown();
  const displayName = vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const assignedDriver = drivers.find((d) => d.id === vehicle.assignedDriverId);
  const assignedDriverName = assignedDriver?.name || null;
  const assignedElsewhereIds = new Set(
    fleetVehicles
      .filter((v) => v.id !== id && v.assignedDriverId)
      .map((v) => v.assignedDriverId),
  );
  const availableDrivers = drivers.filter(
    (d) => d.id === vehicle.assignedDriverId || !assignedElsewhereIds.has(d.id),
  );
  const noDriversInAccount = drivers.length === 0;
  const allDriversAssignedElsewhere = !noDriversInAccount && availableDrivers.length === 0;

  return (
    <div className="page-container">
      <div className="vehicle-details-header">
        <button className="btn-back" onClick={() => navigate('/vehicles')}>
          ← Back to Vehicles
        </button>
        <div className="vehicle-details-title">
          <h1 className="page-title">{displayName}</h1>
          <p className="page-subtitle">
            {vehicle.licensePlate} ({vehicle.licensePlateState}) • VIN: {vehicle.vin}
          </p>
        </div>
        <div className="vehicle-details-actions">
          <button className="btn btn-secondary" onClick={() => setShowDVIRModal(true)}>
            Log DVIR
          </button>
          <button className="btn btn-primary" onClick={() => setShowEditModal(true)}>
            Edit Vehicle
          </button>
        </div>
      </div>

      <div className="vehicle-details-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'dvirs' ? 'active' : ''}`}
          onClick={() => setActiveTab('dvirs')}
        >
          DVIR History ({dvirs.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          Maintenance
        </button>
      </div>

      <div className="vehicle-details-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="readiness-score-card">
              <h3>Readiness Score: {vehicle.readinessScore || 0}/100</h3>
              <div className="readiness-breakdown">
                {readinessBreakdown.map((item, index) => (
                  <div key={index} className="breakdown-item">
                    <span className="breakdown-label">{item.label}</span>
                    <span className={`breakdown-points ${item.earned ? 'earned' : 'not-earned'}`}>
                      {item.earned ? <Check size={14} /> : <X size={14} />} {item.points} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="status-card vehicle-assigned-driver-card">
              <h4>Assigned Driver</h4>
              <div className="vehicle-assigned-driver-row">
                <div className="vehicle-assigned-driver-value">
                  {assignedDriverName ? (
                    <Link
                      to={`/fleet/drivers/${vehicle.assignedDriverId}`}
                      className="vehicle-assigned-driver-link"
                    >
                      {assignedDriverName}
                    </Link>
                  ) : (
                    <span className="vehicle-assigned-driver-unassigned">Unassigned</span>
                  )}
                </div>
                <div className="vehicle-assigned-driver-actions">
                  {assignedDriverName ? (
                    <>
                      <button
                        type="button"
                        className="vehicle-assigned-driver-change"
                        onClick={openAssignFlow}
                        disabled={isSavingAssignment}
                      >
                        Change
                      </button>
                      {!showRemoveConfirm ? (
                        <button
                          type="button"
                          className="vehicle-assigned-driver-remove"
                          onClick={() => {
                            setIsAssignOpen(false);
                            setShowRemoveConfirm(true);
                          }}
                          disabled={isSavingAssignment}
                        >
                          Remove
                        </button>
                      ) : (
                        <div className="vehicle-assigned-driver-confirm">
                          <span>Remove this driver?</span>
                          <button
                            type="button"
                            className="vehicle-assigned-driver-remove"
                            onClick={handleRemoveDriver}
                            disabled={isSavingAssignment}
                          >
                            {isSavingAssignment ? 'Removing…' : 'Yes'}
                          </button>
                          <button
                            type="button"
                            className="vehicle-assigned-driver-change"
                            onClick={() => setShowRemoveConfirm(false)}
                            disabled={isSavingAssignment}
                          >
                            No
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn vehicle-assign-driver-btn"
                      onClick={openAssignFlow}
                      disabled={isSavingAssignment}
                    >
                      Assign Driver
                    </button>
                  )}
                </div>
              </div>

              {isAssignOpen ? (
                <div className="vehicle-assigned-driver-assign">
                  {allDriversAssignedElsewhere ? (
                    <p className="vehicle-assigned-driver-empty">
                      All your drivers are currently assigned to other vehicles. Unassign one first.
                    </p>
                  ) : noDriversInAccount ? (
                    <p className="vehicle-assigned-driver-empty">
                      No drivers to assign yet. Add a driver first.
                    </p>
                  ) : (
                    <>
                      <label className="vehicle-assigned-driver-label" htmlFor="vehicle-driver-select">
                        Select driver
                      </label>
                      <select
                        id="vehicle-driver-select"
                        className="vehicle-assigned-driver-select"
                        value={selectedDriverId}
                        onChange={(e) => setSelectedDriverId(e.target.value)}
                        disabled={isSavingAssignment}
                      >
                        <option value="">Choose a driver…</option>
                        {availableDrivers.map((driver) => (
                          <option key={driver.id} value={driver.id}>
                            {driver.name}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  <div className="vehicle-assigned-driver-assign-actions">
                    {!allDriversAssignedElsewhere && !noDriversInAccount ? (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAssignSave}
                        disabled={!selectedDriverId || isSavingAssignment}
                      >
                        {isSavingAssignment ? 'Saving…' : 'Save'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsAssignOpen(false);
                        setSelectedDriverId('');
                        setAssignmentError('');
                      }}
                      disabled={isSavingAssignment}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}

              {assignmentError ? (
                <p className="vehicle-assigned-driver-error" role="alert">
                  {assignmentError}
                </p>
              ) : null}
            </div>

            <div className="status-cards">
              <div className="status-card">
                <h4>Inspection Status</h4>
                <div className={`status-badge status-inspection-${inspectionStatus.status}`}>
                  {inspectionStatus.label}
                </div>
                {inspectionStatus.days !== null && (
                  <p className="status-countdown">
                    {inspectionStatus.status === 'overdue'
                      ? `${inspectionStatus.days} days overdue`
                      : inspectionStatus.status === 'due_soon'
                      ? `${inspectionStatus.days} days until due`
                      : `${inspectionStatus.days} days remaining`}
                  </p>
                )}
                {vehicle.lastInspectionDate && (
                  <p className="status-date">
                    Last inspection: {new Date(vehicle.lastInspectionDate).toLocaleDateString()}
                  </p>
                )}
                {vehicle.inspectionCertificateUrl && (
                  <a
                    href={`${API_URL}${vehicle.inspectionCertificateUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="document-link"
                  >
                    View Certificate →
                  </a>
                )}
              </div>

              <div className="status-card">
                <h4>Registration Status</h4>
                <div className={`status-badge status-registration-${registrationStatus.status}`}>
                  {registrationStatus.label}
                </div>
                {registrationStatus.days !== null && (
                  <p className="status-countdown">
                    {registrationStatus.status === 'expired'
                      ? `Expired ${registrationStatus.days} days ago`
                      : registrationStatus.status === 'expiring'
                      ? `Expires in ${registrationStatus.days} days`
                      : `${registrationStatus.days} days remaining`}
                  </p>
                )}
                {vehicle.registrationExpirationDate && (
                  <p className="status-date">
                    Expires: {new Date(vehicle.registrationExpirationDate).toLocaleDateString()}
                  </p>
                )}
                {vehicle.registrationDocumentUrl && (
                  <a
                    href={`${API_URL}${vehicle.registrationDocumentUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="document-link"
                  >
                    View Document →
                  </a>
                )}
              </div>
            </div>

            {vehicle.notes && (
              <div className="notes-section">
                <h4>Notes</h4>
                <p>{vehicle.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'dvirs' && (
          <div className="dvirs-tab">
            {dvirs.length === 0 ? (
              <div className="empty-state">
                <p>No inspection reports yet. Log your first DVIR above.</p>
              </div>
            ) : (
              <div className="dvirs-list">
                {dvirs.map((dvir) => (
                  <div key={dvir.id} className="dvir-card">
                    <div className="dvir-header">
                      <div>
                        <span className="dvir-type">
                          {dvir.inspectionType === 'pre-trip' ? <><Circle size={10} fill="var(--success)" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} /> Pre-Trip</> : <><Circle size={10} fill="var(--danger)" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} /> Post-Trip</>}
                        </span>
                        <span className="dvir-date">
                          {new Date(dvir.inspectionDate).toLocaleDateString()}
                        </span>
                      </div>
                      {dvir.hasDefects && (
                        <span className={`defect-badge ${dvir.defectsRepaired ? 'repaired' : 'open'}`}>
                          {dvir.defectsRepaired ? <><Check size={14} /> Repaired</> : <><AlertTriangle size={14} /> Open Defects</>}
                        </span>
                      )}
                    </div>
                    {dvir.hasDefects && (
                      <div className="dvir-defects">
                        <p><strong>Defects:</strong> {dvir.defectsDescription}</p>
                        {dvir.defectsRepaired && dvir.repairDate && (
                          <p>
                            <strong>Repaired:</strong>{' '}
                            {new Date(dvir.repairDate).toLocaleDateString()}
                          </p>
                        )}
                        {dvir.repairNotes && <p><strong>Repair Notes:</strong> {dvir.repairNotes}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="maintenance-tab">
            <div className="empty-state">
              <p>Maintenance tracking coming soon!</p>
            </div>
          </div>
        )}
      </div>

      {showEditModal && (
        <AddVehicleModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={fetchVehicle}
          vehicle={vehicle}
        />
      )}

      {showDVIRModal && (
        <DVIRModal
          isOpen={showDVIRModal}
          onClose={() => setShowDVIRModal(false)}
          vehicle={vehicle}
          onSuccess={fetchDVIRs}
        />
      )}
    </div>
  );
};

export default VehicleDetails;

